import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';
import type { PagedResult } from '../types/pagedAndDtos';

export interface EmailContact {
  id: number;
  name: string;
  email: string;
  sectorId: number;
  tagId: number | null;
  isActive: boolean;
  contactStatus: string;
  isFavorite: boolean;
  archived: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EmailContactListDTO {
  id: number;
  name: string;
  email: string;
  sectorId: number;
  tagId: number | null;
  isActive: boolean;
  contactStatus: string;
  isFavorite: boolean;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEmailContactRequestDTO {
  name: string;
  email: string;
  sectorId: number;
  tagId?: number | null;
  isActive?: boolean;
  contactStatus?: string;
  notes?: string;
}

export interface UpdateEmailContactRequestDTO {
  name: string;
  email: string;
  tagId?: number | null;
  isActive?: boolean;
  contactStatus?: string;
  notes?: string;
  isFavorite?: boolean;
  archived?: boolean;
}

export interface ImportEmailContactDTO {
  name: string;
  email: string;
  sectorId: number;
}

export interface ImportEmailContactsRequestDTO {
  sectorId: number;
  contacts: ImportEmailContactDTO[];
}

const getAuthHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  return { Authorization: `Bearer ${token}` };
};

export const getEmailContactsPaged = async (
  sectorId: number,
  pageNumber: number = 1,
  pageSize: number = 50,
  search?: string,
  archived?: boolean
): Promise<PagedResult<EmailContactListDTO>> => {
  const params: Record<string, number | string | boolean> = { sectorId, pageNumber, pageSize };
  if (search != null && search.trim() !== '') params.search = search.trim();
  if (archived != null) params.archived = archived;

  const response = await axios.get(`${API_URL}/EmailContact/paged`, {
    params,
    headers: getAuthHeaders(),
  });
  const d = response.data;
  return {
    items: d?.items ?? d?.Items ?? [],
    totalCount: d?.totalCount ?? d?.TotalCount ?? 0,
    pageNumber: d?.pageNumber ?? d?.PageNumber ?? 1,
    pageSize: d?.pageSize ?? d?.PageSize ?? 50,
    totalPages: d?.totalPages ?? d?.TotalPages ?? 0,
    hasNextPage: d?.hasNextPage ?? d?.HasNextPage ?? false,
    hasPreviousPage: d?.hasPreviousPage ?? d?.HasPreviousPage ?? false,
  };
};

type EmailApiEnvelope<T> = { data?: T; Data?: T };

export const getEmailContactsBySector = async (sectorId: number): Promise<EmailContact[]> => {
  const response = await axios.get<EmailApiEnvelope<EmailContact[]>>(`${API_URL}/EmailContact/sector/${sectorId}`, {
    headers: getAuthHeaders(),
  });
  const body = response.data;
  return body?.data ?? body?.Data ?? [];
};

export const getEmailContactById = async (id: number): Promise<EmailContact | null> => {
  const response = await axios.get<EmailApiEnvelope<EmailContact>>(`${API_URL}/EmailContact/${id}`, {
    headers: getAuthHeaders(),
  });
  const body = response.data;
  return body?.data ?? body?.Data ?? null;
};

export const createEmailContact = async (dto: CreateEmailContactRequestDTO): Promise<EmailContact> => {
  const response = await axios.post<EmailApiEnvelope<EmailContact> | EmailContact>(`${API_URL}/EmailContact`, dto, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
  });
  const body = response.data;
  if (body && typeof body === 'object' && ('data' in body || 'Data' in body)) {
    const env = body as EmailApiEnvelope<EmailContact>;
    return env.data ?? env.Data ?? (body as EmailContact);
  }
  return body as EmailContact;
};

export const updateEmailContact = async (id: number, dto: UpdateEmailContactRequestDTO): Promise<EmailContact> => {
  const response = await axios.put<EmailApiEnvelope<EmailContact> | EmailContact>(`${API_URL}/EmailContact/${id}`, dto, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
  });
  const body = response.data;
  if (body && typeof body === 'object' && ('data' in body || 'Data' in body)) {
    const env = body as EmailApiEnvelope<EmailContact>;
    return env.data ?? env.Data ?? (body as EmailContact);
  }
  return body as EmailContact;
};

export const deleteEmailContact = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/EmailContact/${id}`, { headers: getAuthHeaders() });
};

export const importEmailContacts = async (dto: ImportEmailContactsRequestDTO): Promise<EmailContact[]> => {
  const response = await axios.post(`${API_URL}/EmailContact/import`, dto, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
  });
  const d = response.data;
  const arr = d?.data ?? d?.Data ?? d;
  return Array.isArray(arr) ? arr : [];
};

export const importEmailContactsFromFile = async (file: File, sectorId: number): Promise<EmailContact[]> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('sectorId', sectorId.toString());

  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');

  try {
    // Use axios (not fetch): multipart boundary must be set by the client; mixing fetch + large uploads
    // often surfaces as "Failed to fetch" when Kestrel/IIS rejects the body before a JSON error is returned.
    const response = await axios.post<unknown>(`${API_URL}/EmailContact/import-file`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      maxBodyLength: Number.POSITIVE_INFINITY,
      maxContentLength: Number.POSITIVE_INFINITY,
    });
    const d = response.data as { data?: EmailContact[]; Data?: EmailContact[] };
    const arr = d?.data ?? d?.Data;
    return Array.isArray(arr) ? arr : [];
  } catch (err) {
    if (axios.isAxiosError(err)) {
      if (err.response == null) {
        throw new Error(
          'Não foi possível conectar à API (importação). Verifique REACT_APP_API_URL, se o backend está no ar, CORS/HTTPS e o tamanho do ficheiro (limite no servidor ~100MB).'
        );
      }
      const data = err.response.data;
      let msg = 'Import failed';
      if (data && typeof data === 'object') {
        const o = data as { message?: string; Message?: string };
        msg = o.message || o.Message || msg;
      } else if (typeof data === 'string' && data.trim()) msg = data;
      throw new Error(msg);
    }
    throw err;
  }
};

export const downloadEmailContactTemplate = async (): Promise<Blob> => {
  const response = await fetch(`${API_URL}/EmailContact/template`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Error downloading template');
  return response.blob();
};
